class ProductComparisonDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#comparison-drawer-overlay').addEventListener('click', this.close.bind(this));
    
    // Add this line to handle keydown events within the drawer
    this.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.querySelector('.drawer__inner');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  // Add this method to handle keydown events
  handleKeyDown(event) {
    if (event.target.matches('.comparison-checkbox input[data-compare-id]')) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.target.checked = !event.target.checked;
        event.target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }
}

customElements.define('product-comparison-drawer', ProductComparisonDrawer);

document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.querySelector('product-comparison-drawer');
  if (drawer) {
    document.body.appendChild(drawer);
  }
});

class ProductComparison {
  constructor() {
    this.comparisonChart = document.querySelector('[data-product-comparison]');
    this.chartContainer = document.getElementById('comparison-chart');
    this.drawer = document.querySelector('product-comparison-drawer');
    if (this.comparisonChart) {
      this.hideChart(); // Hide the chart initially
      this.init();
    }
  }

  init() {
    const productList = this.getProductList();
    const productIDs = this.getURLParameter('pids');

    if (productIDs === 'all') {
      // Remove this line to prevent clearing the localStorage
      // localStorage.removeItem('compareList');
      this.displayChart();
    } else if (productIDs) {
      localStorage.setItem('compareList', productIDs);
      this.displayChart(productIDs.split(','));
    } else if (productList.length > 0) {
      this.displayChart(productList);
    } else {
      this.displayChart();
    }

    this.showChartInfo();
    this.detectHorizontalScroll();
    this.showChart(); // Show the chart after initialization
  }

  displayChart(productList = []) {
    const cells = Array.from(this.comparisonChart.querySelectorAll('td, th'));
    const productCellsMap = new Map();

    // Hide all product cells first
    cells.forEach(cell => {
      if (cell.hasAttribute('data-product-id')) {
        cell.style.display = 'none';
        const productId = cell.getAttribute('data-product-id');
        if (!productCellsMap.has(productId)) {
          productCellsMap.set(productId, []);
        }
        productCellsMap.get(productId).push(cell);
      } else {
        cell.style.display = 'table-cell';
      }
    });

    // console.log('Product list to display:', productList);

    // Show specific products or all products if the list is empty
    if (productList.length > 0) {
      productList.forEach(productId => {
        const cleanProductId = productId.replace(/["\[\]]/g, '');
        const productCells = productCellsMap.get(cleanProductId) || [];
        productCells.forEach(cell => cell.style.display = 'table-cell');
      });
    } else {
      // Show all products
      cells.forEach(cell => cell.style.display = 'table-cell');
    }
  }

  getProductList() {
    const compareList = localStorage.getItem('compareList');
    // console.log('Comparison list from localStorage:', compareList);
    try {
      const parsedList = compareList ? JSON.parse(compareList) : [];
      return parsedList.filter(productId => productId); // Filter out empty strings
    } catch (e) {
      console.error('Error parsing comparison list from localStorage:', e);
      return [];
    }
  }

  getURLParameter(name) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  }

  showChartInfo() {
    const chartInfo = document.querySelectorAll('[data-chart-info]');
    chartInfo.forEach(el => el.style.display = 'block');
  }

  detectHorizontalScroll() {
    const productChart = this.comparisonChart.querySelector('.product-chart__table');
    if (productChart.offsetWidth > this.comparisonChart.offsetWidth) {
      this.comparisonChart.classList.add('has-scrollbar-y');
    }
  }

  showChart() {
    if (this.chartContainer) {
      this.chartContainer.style.visibility = 'visible';
    }
  }

  hideChart() {
    if (this.chartContainer) {
      this.chartContainer.style.visibility = 'hidden';
    }
  }
}

// Function to toggle the comparison sidebar
function toggleComparisonSidebar(triggeredBy) {
  // console.log('toggleComparisonSidebar called');
  const drawer = document.querySelector('product-comparison-drawer');
  if (drawer) {
    if (drawer.classList.contains('active')) {
      drawer.close();
    } else {
      drawer.open(triggeredBy);
    }
    // console.log('Drawer class list:', drawer.classList);
  } else {
    console.error('Drawer element not found');
  }
}

// Function to refresh the comparison table and close the sidebar
function refreshComparison() {
  const comparison = new ProductComparison();
  const productList = comparison.getProductList();
  
  // Use the product list from localStorage if available
  if (productList.length > 0) {
    comparison.displayChart(productList);
  } else {
    comparison.init();
  }

  // Close the sidebar after refreshing the comparison table
  const drawer = document.querySelector('product-comparison-drawer');
  if (drawer) {
    drawer.close();
  }
}

customElements.define('product-comparison', class extends HTMLElement {
  connectedCallback() {
    new ProductComparison();
  }
});

// Utility functions for focus trapping
function trapFocus(container, elementToFocus) {
  const focusableElements = container.querySelectorAll(
    'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable]'
  );
  const firstFocusableElement = focusableElements[0];
  const lastFocusableElement = focusableElements[focusableElements.length - 1];

  function handleKeyDown(event) {
    if (event.key === 'Tab') {
      if (event.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          event.preventDefault();
          lastFocusableElement.focus();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          event.preventDefault();
          firstFocusableElement.focus();
        }
      }
    }
    // Allow other key events to propagate
  }

  container.addEventListener('keydown', handleKeyDown);

  elementToFocus.focus();

  return () => {
    container.removeEventListener('keydown', handleKeyDown);
  };
}

function removeTrapFocus(activeElement) {
  if (activeElement) {
    activeElement.focus();
  }
}